import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { useHttpClient } from '~/hooks/useHttpClient';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { createLogger } from '~/utils/logging';
import { REVIEW_DAY_STATUS_QUERY_KEY } from '~/services/review';

const log = createLogger('useSessionRequestCloseMutation');

export type SessionRequestCloseResponse = {
  containerId: string;
  status: 'closed' | 'accepted';
};

/** `POST /session/requestClose` — teacher-initiated close-now for the currently open
 *  session container. No request body: the authenticated teacher context selects the
 *  container. A 404 means nothing is open to close (the idle CTA shouldn't be visible in
 *  that state anyway) — treated as a no-op, not surfaced as an error, per the "the click
 *  always succeeds from the teacher's perspective" UX contract. */
export function useSessionRequestCloseMutation() {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { mutate, status } = useMutation({
    mutationFn: async () => {
      log.debug('Requesting session close');
      const response = await httpClient.post<SessionRequestCloseResponse>('/session/requestClose', {});
      log.info('Requested session close successfully', { status: response.status });
      return response;
    },
    onSettled: () => {
      // Requesting close only kicks off async processing — it doesn't produce fresh review
      // data synchronously, so there's nothing yet for the dependent review queries (scorecards,
      // action items, etc.) to gain from refetching now. Just nudge the day-status query so the
      // banner reflects the new state promptly; `useReviewDayStatus` cascades the rest once that
      // status actually transitions (e.g. idle -> processing -> processed).
      queryClient.invalidateQueries({ queryKey: [REVIEW_DAY_STATUS_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // A 404 (nothing open to close) is an expected race, not an error to report.
      } else {
        log.error('Failed to request session close', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return { mutate, status };
}
